/**
 * Raw inventory CSV projection.
 *
 * The catalog is canonical (one inventory_items row can own several vendor
 * purchase_options), but a raw inventory download is a vendor-product ledger.
 * Expanding active options here preserves the source cardinality: twenty
 * imported vendor products export as twenty rows even after ten related pairs
 * are grouped under ten canonical items.
 */

export const RAW_INVENTORY_CSV_COLUMNS = [
  ['Name', 'name'],
  ['Vendor', 'vendor'],
  ['Category', 'category'],
  ['Date Added', 'dateAdded'],
  ['Area', 'area'],
  ['Brand', 'brand'],
  ['Pack/Size', 'packSize'],
  // Preserve the historical export labels/order for downstream spreadsheets.
  // Their values now come from the vendor product instead of a blended item.
  ['Unit', 'countUnit'],
  ['Case Qty', 'qtyPerPackage'],
  ['Split Qty', 'splitQty'],
  ['Case $', 'packagePrice'],
  ['Unit $', 'unitPrice'],
  ['Code', 'code'],
  ['Canonical Item', 'canonicalItem'],
  ['Purchase Unit', 'purchaseUnit'],
];

const text = (value) => String(value ?? '').trim();

const optionVendor = (option, item, vendorNameById) => {
  if (option?.vendor_id != null && vendorNameById?.[option.vendor_id]) {
    return text(vendorNameById[option.vendor_id]);
  }
  return text(option?.vendor_name_text) || text(item?.vendor);
};

const itemValue = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
  }
  return '';
};

const baseFields = (item) => ({
  canonicalItem: text(item?.description),
  category: text(item?.category),
  area: text(item?.location),
  brand: text(item?.brand),
  countUnit: text(item?.unit),
  splitQty: itemValue(item, 'split_qty', 'split_quantity'),
});

const fallbackRow = (item) => ({
  ...baseFields(item),
  name: text(item?.description),
  vendor: text(item?.vendor),
  dateAdded: item?.created_at || '',
  packSize: text(item?.pack_size),
  purchaseUnit: text(item?.unit),
  qtyPerPackage: itemValue(item, 'case_qty', 'case_quantity'),
  packagePrice: itemValue(item, 'case_price'),
  unitPrice: itemValue(item, 'unit_price', 'each_price'),
  code: text(itemValue(item, 'item_code', 'manufacturer_code', 'supc')),
});

/**
 * Build one raw row per active purchase option, falling back to one canonical
 * row only for legacy items that do not have a purchase option yet.
 */
export function buildRawInventoryCsvRows(items, optionsByItemId = {}, vendorNameById = {}) {
  return (Array.isArray(items) ? items : []).flatMap((item) => {
    const options = (optionsByItemId?.[item?.id] || [])
      .filter((option) => option && option.status !== 'inactive');
    if (!options.length) return [fallbackRow(item)];
    return options.map((option) => ({
      ...baseFields(item),
      name: text(option.vendor_description) || text(item?.description),
      vendor: optionVendor(option, item, vendorNameById),
      dateAdded: option.created_at || item?.created_at || '',
      packSize: text(option.pack_description) || text(item?.pack_size),
      purchaseUnit: text(option.purchase_unit) || text(item?.unit),
      qtyPerPackage: option.qty_per_package ?? '',
      packagePrice: option.package_price ?? '',
      unitPrice: option.normalized_unit_cost ?? '',
      code: text(option.vendor_sku) || text(itemValue(item, 'item_code', 'manufacturer_code', 'supc')),
    }));
  });
}
